import React, { useState } from "react";
import {
  Button,
  Box,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  DeleteForever as DeleteForeverIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { AlertDialog } from "../../../../../components/Dialog/AlertDialog";
import { api } from "../../../../../Assets/api/api";
import { toast } from "react-toastify";
import FileExplorerHeader from "./FileExplorerHeader";
import { useTranslation } from "react-i18next";
import { useSearchSort } from "../context/SearchSortContext";

const RecycleBinHeader = ({ onBackToFiles, slug, ...props }) => {
  const { t } = useTranslation();
  const translation = t("fileExplorer");

  // Get search state from context (sorting removed)
  const { searchText, setSearchText } = useSearchSort();

  const [selectedTypes, setSelectedTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEmptyBinDialogOpen, setIsEmptyBinDialogOpen] = useState(false);

  // Validate slug before proceeding
  if (!slug || typeof slug !== "string" || slug.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          User information not available
        </Typography>
      </Box>
    );
  }

  const emptyRecycleBin = async () => {
    try {
      setLoading(true);
      const response = await api.delete(
        `/api/account-storage/recycle-bin/empty${slug}`
      );

      if (response?.data?.success) {
        toast.success(
          response?.data?.message || "Recycle bin emptied successfully"
        );
        if (props.onActionComplete) {
          props.onActionComplete();
        }
      } else {
        toast.error(response?.data?.message || "Failed to empty recycle bin");
      }
    } catch (error) {
      console.error("Error emptying recycle bin:", error);
      toast.error(
        error?.response?.data?.message || "Error emptying recycle bin"
      );
    } finally {
      setLoading(false);
      setIsEmptyBinDialogOpen(false);
    }
  };

  // Create the search control (sorting removed)
  const searchControls = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
      <TextField
        placeholder={translation.searchPlaceholder}
        size="small"
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ width: 200 }}
      />
    </Box>
  );

  const emptyBinButton = (
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteForeverIcon />}
      onClick={() => setIsEmptyBinDialogOpen(true)}
      disabled={loading}
    >
      {translation.emptyRecycleBin}
    </Button>
  );

  // Combine the right side controls
  const rightContent = (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {searchControls}
      {emptyBinButton}
    </Box>
  );

  return (
    <>
      <FileExplorerHeader
        onBackClick={onBackToFiles}
        title={translation.recycleBin}
        rightContent={rightContent}
        leftContent={props.leftContent}
      />

      <AlertDialog
        open={isEmptyBinDialogOpen}
        loading={loading}
        handleClose={() => !loading && setIsEmptyBinDialogOpen(false)}
        handleOk={emptyRecycleBin}
        warningMessage={translation.confirmEmptyRecycleBin}
        topMessage={translation.emptyRecycleBinTitle}
        undoMessage={translation.actionCannotBeUndone}
      />
    </>
  );
};

export default RecycleBinHeader;
